import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { forgetAreas, savedAreas } from '../utils/offline/basemap';
import { deviceByteStore } from '../utils/offline/byteStore';
import { CACHE_PREFIX } from '../utils/routing/tiles';

/*
 * The account page's "Offline maps on this device": the areas saved for
 * offline use (components/map/SaveAreaButton), how much map data the device
 * holds, and one button that removes all of it -- the base map's byte ranges
 * (IndexedDB) and the routing files (Cache Storage). They download again
 * when needed online.
 */
const MB = 1_000_000;

export async function removeAllOfflineData({ store = deviceByteStore(), cachesApi = typeof caches !== 'undefined' ? caches : null } = {}) {
  await store.clear();
  if (cachesApi) {
    for (const name of await cachesApi.keys()) if (name.startsWith(CACHE_PREFIX)) await cachesApi.delete(name);
  }
  forgetAreas();
}

export default function OfflineAreasPanel({ store = null }) {
  const { t, i18n } = useTranslation('offline');
  const [areas, setAreas] = useState(() => savedAreas());
  const [usage, setUsage] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let live = true;
    (store || deviceByteStore()).usage().then((u) => { if (live) setUsage(u); }).catch(() => {});
    return () => { live = false; };
  }, [store, done]);

  const removeAll = async () => {
    await removeAllOfflineData(store ? { store } : undefined);
    setAreas([]);
    setDone(true);
  };

  const day = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(i18n?.language === 'he' ? 'he-IL' : 'en-GB');
    } catch (e) {
      return iso;
    }
  };

  return (
    <section data-testid="offline-areas" style={{ border: '1px solid var(--c-border)', borderRadius: 8, padding: 16,
      marginBottom: 16, background: 'var(--c-surface)' }}>
      <h3 style={{ marginTop: 0 }}>{t('panel.title')}</h3>
      <p style={{ color: 'var(--c-text-muted-fg)', marginTop: 0, fontSize: 14 }}>{t('panel.intro')}</p>
      {areas.length === 0
        ? <p data-testid="offline-areas-none" style={{ fontSize: 14 }}>{t('panel.none')}</p>
        : (
          <ul style={{ paddingInlineStart: 18, fontSize: 14 }} data-testid="offline-areas-list">
            {areas.map((a) => (
              <li key={a.id}><strong>{a.label || a.id}</strong> -- {t('panel.saved', { date: day(a.savedAt), tiles: a.tiles })}</li>
            ))}
          </ul>
        )}
      {usage && usage.bytes > 0 && (
        <p style={{ fontSize: 13 }} data-testid="offline-areas-usage">
          {t('panel.usage', { mb: Math.max(1, Math.round(usage.bytes / MB)) })}
        </p>
      )}
      <button type="button" onClick={removeAll} title={t('tip.removeAll')} data-testid="offline-areas-remove"
        style={{ minHeight: 40, padding: '0 14px', borderRadius: 6, border: '1px solid var(--c-border)',
          background: 'var(--c-surface)', color: 'var(--c-text-fg)', cursor: 'pointer', fontSize: 14 }}>
        {t('panel.removeAll')}
      </button>
      {done && <p role="status" style={{ fontSize: 13 }}>{t('panel.removed')}</p>}
    </section>
  );
}
